import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Card,
  LinearProgress,
  Fade,
  Grow,
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  Rocket as RocketIcon,
  Palette as PaletteIcon,
  BugReport as BugIcon,
  Lightbulb as TipIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { getSupabaseClient } from '../../services/supabase';

interface Notification {
  id: number;
  title: string;
  message: string;
  category: string;
  icon: string;
  feature_date: string;
  action_type?: string;
  action_target?: string;
}

interface WelcomeBackModalProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
  userName?: string;
}

const categoryConfig = {
  new_features: {
    icon: <AutoAwesomeIcon />,
    color: '#9c27b0',
    bgColor: 'rgba(156, 39, 176, 0.1)',
    label: 'New Magic Powers'
  },
  performance: {
    icon: <RocketIcon />,
    color: '#ff5722',
    bgColor: 'rgba(255, 87, 34, 0.1)',
    label: 'Performance Boosts'
  },
  visual_updates: {
    icon: <PaletteIcon />,
    color: '#2196f3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    label: 'Visual Improvements'
  },
  bug_fixes: {
    icon: <BugIcon />,
    color: '#4caf50',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    label: 'Bug Squashes'
  },
  pro_tips: {
    icon: <TipIcon />,
    color: '#ff9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    label: 'Pro Tips'
  }
};

export const WelcomeBackModal: React.FC<WelcomeBackModalProps> = ({
  open,
  onClose,
  userEmail,
  userName
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (open && userEmail) {
      fetchUnseenNotifications();
    }
  }, [open, userEmail]);

  const fetchUnseenNotifications = async () => {
    setLoading(true);
    try {
      const supabase = await getSupabaseClient(true);
      
      // Get current date
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch active notifications that the user hasn't dismissed
      const { data: notifications, error } = await (supabase as any)
        .from('user_notifications')
        .select('*')
        .eq('is_active', true)
        .lte('show_after_date', today)
        .or(`expires_date.is.null,expires_date.gte.${today}`)
        .order('feature_date', { ascending: false });

      if (error) throw error;

      if (notifications && notifications.length > 0) {
        // Check which ones user has already dismissed
        const { data: dismissals } = await (supabase as any)
          .from('user_notification_dismissals')
          .select('notification_id')
          .eq('user_email', userEmail);

        const dismissedIds = new Set(dismissals?.map((d: any) => d.notification_id) || []);
        const unseenNotifications = notifications.filter((n: any) => !dismissedIds.has(n.id));
        
        setNotifications(unseenNotifications);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (notifications.length === 0) return;
    
    setDismissing(true);
    try {
      const supabase = await getSupabaseClient(true);
      
      // Mark all shown notifications as dismissed
      const dismissals = notifications.map(n => ({
        user_email: userEmail,
        notification_id: n.id
      }));

      await (supabase as any)
        .from('user_notification_dismissals')
        .upsert(dismissals, { onConflict: 'user_email,notification_id' });

      onClose();
    } catch (error) {
      console.error('Error dismissing notifications:', error);
    } finally {
      setDismissing(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < notifications.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const currentNotification = notifications[currentIndex];
  const config = currentNotification ? categoryConfig[currentNotification.category as keyof typeof categoryConfig] : null;

  const displayName = userName || userEmail.split('@')[0];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'visible',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }
      }}
    >
      {/* Sparkle decorations */}
      <Box sx={{
        position: 'absolute',
        top: -10,
        right: 20,
        color: 'white',
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)', opacity: 1 },
          '50%': { transform: 'scale(1.1)', opacity: 0.7 },
          '100%': { transform: 'scale(1)', opacity: 1 }
        },
        animation: 'pulse 2s infinite'
      }}>
        <AutoAwesomeIcon sx={{ fontSize: 30 }} />
      </Box>
      <Box sx={{
        position: 'absolute',
        top: 20,
        left: -10,
        color: 'white',
        '@keyframes pulse2': {
          '0%': { transform: 'scale(1)', opacity: 1 },
          '50%': { transform: 'scale(1.1)', opacity: 0.7 },
          '100%': { transform: 'scale(1)', opacity: 1 }
        },
        animation: 'pulse2 2s infinite',
        animationDelay: '1s'
      }}>
        <AutoAwesomeIcon sx={{ fontSize: 24 }} />
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, color: 'white' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Welcome back, {displayName}! 🦄
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {loading ? 'Checking for updates...' : 
                 notifications.length === 0 ? 'You\'re all caught up!' :
                 'While you were away, we added some magical new features!'}
              </Typography>
            </Box>
            <IconButton 
              onClick={handleDismiss} 
              size="small" 
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          {loading ? (
            <Box sx={{ py: 4 }}>
              <LinearProgress sx={{ bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
            </Box>
          ) : notifications.length === 0 ? (
            <Fade in={true}>
              <Card sx={{ 
                p: 3, 
                textAlign: 'center',
                bgcolor: 'rgba(255,255,255,0.95)',
                color: 'text.primary'
              }}>
                <Typography variant="h1" sx={{ fontSize: 64, mb: 2 }}>
                  🦄💤
                </Typography>
                <Typography variant="h6" gutterBottom>
                  No new updates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The unicorn is sleeping... you've already seen all the latest features!
                </Typography>
              </Card>
            </Fade>
          ) : (
            <Grow in={true}>
              <Box>
                {/* Progress dots */}
                {notifications.length > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                    {notifications.map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: index === currentIndex ? 'white' : 'rgba(255,255,255,0.3)',
                          transition: 'all 0.3s',
                          cursor: 'pointer'
                        }}
                        onClick={() => setCurrentIndex(index)}
                      />
                    ))}
                  </Box>
                )}

                {/* Notification card */}
                {currentNotification && config && (
                  <Card sx={{ 
                    bgcolor: 'rgba(255,255,255,0.95)',
                    color: 'text.primary',
                    position: 'relative',
                    overflow: 'visible'
                  }}>
                    {/* Category badge */}
                    <Box sx={{
                      position: 'absolute',
                      top: -12,
                      left: 20,
                      px: 2,
                      py: 0.5,
                      bgcolor: config.color,
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      {React.cloneElement(config.icon, { sx: { fontSize: 16 } })}
                      {config.label}
                    </Box>

                    <Box sx={{ p: 3, pt: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, mb: 2 }}>
                        <Typography variant="h3" sx={{ mt: -1 }}>
                          {currentNotification.icon}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            {currentNotification.title}
                          </Typography>
                          <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                            {currentNotification.message}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Navigation buttons */}
                      {notifications.length > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                          <Button
                            size="small"
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                            sx={{ minWidth: 80 }}
                          >
                            Previous
                          </Button>
                          <Typography variant="caption" sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: 'text.secondary'
                          }}>
                            {currentIndex + 1} of {notifications.length}
                          </Typography>
                          <Button
                            size="small"
                            onClick={handleNext}
                            disabled={currentIndex === notifications.length - 1}
                            endIcon={<ArrowForwardIcon />}
                            sx={{ minWidth: 80 }}
                          >
                            Next
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Card>
                )}
              </Box>
            </Grow>
          )}
        </Box>
      </DialogContent>

      {!loading && notifications.length > 0 && (
        <DialogActions sx={{ bgcolor: 'rgba(0,0,0,0.1)', px: 3, py: 2 }}>
          <Button 
            onClick={handleDismiss}
            variant="contained"
            disabled={dismissing}
            sx={{ 
              bgcolor: 'white',
              color: '#7b61ff',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
            }}
          >
            {dismissing ? 'Saving...' : 'Got it, thanks!'}
          </Button>
        </DialogActions>
      )}

    </Dialog>
  );
};